import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import {
  LayoutDashboard,
  Users,
  Package,
  Briefcase,
  FileText,
  DollarSign,
  Heart,
  ShoppingCart,
  Calendar,
  CalendarClock,
  CalendarDays,
  MapPinned,
  BriefcaseBusiness,
  Warehouse,
  Store,
  LogOut,
  Menu,
  X,
  UserRound,
  ChevronDown,
} from 'lucide-react';
import { useState } from 'react';

const appName = 'KawaTenda';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navSections = [
    {
      label: 'Geral',
      items: [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', roles: ['ADMIN', 'USER'] },
        { to: '/customers', icon: Users, label: 'Clientes', roles: ['ADMIN'] },
        { to: '/pdv', icon: ShoppingCart, label: 'PDV', roles: ['ADMIN', 'USER'] },
        { to: '/meu-dia', icon: CalendarDays, label: 'MeuDia', roles: ['ADMIN', 'USER'] },
        { to: '/agendas', icon: Calendar, label: 'Agenda', roles: ['ADMIN', 'USER'] },
      ],
    },
    {
      label: 'Faturamento',
      items: [
        { to: '/invoices', icon: FileText, label: 'Faturamento', roles: ['ADMIN', 'USER'] },
        { to: '/expenses', icon: DollarSign, label: 'Despesas', roles: ['ADMIN'] },
        { to: '/donations', icon: Heart, label: 'Doações', roles: ['ADMIN'] },
      ],
    },
    {
      label: 'Cadastros',
      items: [
        { to: '/stock', icon: Warehouse, label: 'Estoque', roles: ['ADMIN'] },
        { to: '/users', icon: Users, label: 'Usuários', roles: ['ADMIN'] },
        { to: '/pdv-locations', icon: Store, label: 'Cadastro PDV', roles: ['ADMIN'] },
        { to: '/carteiras', icon: MapPinned, label: 'Carteira', roles: ['ADMIN'] },
        { to: '/vendedores', icon: UserRound, label: 'Vendedores', roles: ['ADMIN'] },
        { to: '/customer-territories', icon: BriefcaseBusiness, label: 'Clientes por Carteira', roles: ['ADMIN'] },
        { to: '/visit-configs', icon: CalendarClock, label: 'Configuração Visita', roles: ['ADMIN', 'USER'] },
        { to: '/products', icon: Package, label: 'Produtos', roles: ['ADMIN', 'USER'] },
        { to: '/services', icon: Briefcase, label: 'Serviços', roles: ['ADMIN', 'USER'] },
      ],
    },
  ];

  const filteredNavSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.roles.includes(user?.role)),
    }))
    .filter((section) => section.items.length > 0);

  const NavItem = ({ item, mobile = false }) => (
    <NavLink
      to={item.to}
      onClick={() => mobile && setSidebarOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
          isActive
            ? 'bg-primary text-primary-foreground shadow-md'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        }`
      }
      data-testid={`nav-${item.label.toLowerCase()}`}
    >
      <item.icon className="w-5 h-5" />
      <span className="font-medium">{item.label}</span>
    </NavLink>
  );

  const NavSection = ({ section }) => {
    const open = Boolean(openSections[section.label]);

    return (
      <Collapsible
        open={open}
        onOpenChange={(nextOpen) =>
          setOpenSections((current) => ({ ...current, [section.label]: nextOpen }))
        }
        className="space-y-1"
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-lg px-4 py-2 text-xs font-semibold uppercase text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            data-testid={`nav-section-${section.label.toLowerCase()}`}
          >
            <span>{section.label}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1">
          {section.items.map((item) => (
            <NavItem key={item.to} item={item} mobile />
          ))}
        </CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-card border-b shadow-sm">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-2">
            <img src="/icon.svg" alt="" className="w-7 h-7" />
            <span className="font-bold text-lg">{appName}</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-button"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-40 h-full w-72 bg-card border-r shadow-lg
          transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="sidebar"
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center gap-3 px-6 border-b">
          <img src="/icon.svg" alt="" className="w-10 h-10 rounded-xl" />
          <div className="flex-1">
            <h1 className="font-bold text-lg">{appName}</h1>
            <p className="text-xs text-muted-foreground">{user?.name}</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-5 overflow-y-auto h-[calc(100vh-128px)]">
          {filteredNavSections.map((section) => (
            <NavSection key={section.label} section={section} />
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-card">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <span className="text-sm font-bold text-primary">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground">
                {user?.role === 'ADMIN' ? 'Administrador' : 'Usuário'}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-72 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
